import { FC, useRef, useState } from 'react';

import styled, { css } from 'styled-components';

import { callService } from '@api/caller';
import Tab, { TabItem } from '@components/ui/tab/Tab';
import { LANDING_BREAKPOINT } from '@utils/theme/breakPoint';
import { addTourSearchBarItemClickGTM } from '@utils/gtm/tour-gtm';
import { formatArrayToString } from '@utils/utils';
import { useOutsideClick } from '@hooks/hooks';
import Loader from '@components/Loader/Loader';

import HotelTabContent, { HotelBookerError } from './HotelTabContent';
import BookerWheretoGo, { TourBookerError } from './common/BookerWheretoGo';
import FlightTabContent, { FlightBookerError } from './FlightTabContent';
import BookerSearchButton from './common/BookerSearchButton';
import { BookerTabItems } from './common/bookerUtils';
import CruiseTabContent from './CruiseTabContent';

export const BOOKER_SIZE = '570px';

export const urlByFilterItems = (
  page: string,
  items: {
    [key: string]: string | number | undefined;
  }
) => {
  let url = `${page}?`;
  Object.keys(items).forEach((key) => {
    if (key && items[key]) {
      url += `${key}=${items[key]}&`;
    }
  });
  url = url.slice(0, -1);
  return url;
};

export const BookerContainer = styled.div<{ hasError: boolean }>`
  z-index: -1;
  position: relative;
  width: 100%;
  opacity: 0.99;
  background-color: ${({ theme }) => theme.palette.lynxWhite};
  box-shadow: 0 20px 40px 0 rgba(58, 58, 58, 0.2);
  @media (min-width: ${LANDING_BREAKPOINT}) {
    width: ${BOOKER_SIZE};
  }
  @media (max-width: ${LANDING_BREAKPOINT}) {
    ${({ hasError }) =>
      hasError &&
      css`
        position: absolute;
        top: 223px;
        width: 100%;
      `}
  }
`;

const HomePageBookerContainer = styled.div<{ bottom?: number }>`
  position: relative;
  width: 100%;
  margin-bottom: 50px;
  z-index: 10;

  @media (min-width: ${LANDING_BREAKPOINT}) {
    max-width: fit-content;
    margin-top: 46px;
    margin-left: 100px;
    margin-bottom: ${({ bottom }) => (bottom ? `${bottom}px` : '100px')};
  }
`;

export const BOOKER_TABS = {
  HOTEL: 0,
  FLIGHT: 1,
  TOUR: 2,
  CRUISE: 3,
};

export type BookerProps = {
  hasError?: boolean;
};

export const Booker: FC<BookerProps> = ({ hasError = false }) => {
  const [href, setHref] = useState<string>('');
  const [selected, setselected] = useState(BOOKER_TABS.HOTEL);
  const [tabType, setTabType] = useState<string>('');
  const [hotelError, setHotelError] = useState<HotelBookerError | undefined>({
    location: false,
  });
  const [flightError, setFlightError] = useState<
    FlightBookerError | undefined
  >();
  const [tourError, setTourError] = useState<TourBookerError | undefined>({
    location: false,
  });

  const [initialFlightError, setInitialFlightError] = useState<
    FlightBookerError | undefined
  >();
  const [initialHotelError, setInitialHotelError] = useState<
    HotelBookerError | undefined
  >({
    location: true,
  });
  const [initialTourError, setInitialTourError] = useState<
    TourBookerError | undefined
  >({
    location: true,
  });

  const [loading, setLoading] = useState<boolean>(false);

  // ===============================================
  // Toggle active tab (hotel , tour , flight)
  // ===============================================
  const onTabSelect = (s: number) => {
    setHotelError({});
    setFlightError({});
    setTourError({});
    setselected(s);
    setHref('');
  };

  // ===============================================
  // Handle updating the href
  // ===============================================
  const onHrefUpdate = (href: string) => {
    setHref(href);
  };

  // ===============================================
  // Handle Clicking on the search button
  // ===============================================
  const handleSearchButtonClick = async () => {
    setHotelError(initialHotelError);
    setFlightError(initialFlightError);
    setTourError(initialTourError);

    if (selected === BOOKER_TABS.HOTEL && initialHotelError?.location) return;
    if (selected === BOOKER_TABS.TOUR && initialTourError?.location) return;

    if (
      selected === BOOKER_TABS.FLIGHT &&
      (initialFlightError?.location?.arriving ||
        initialFlightError?.location?.departure ||
        initialFlightError?.date)
    )
      return;

    if (
      href?.includes('ic-hat-ucuslari') ||
      href?.includes('dis-hat-ucuslari')
    ) {
      setLoading(true);
      return window.open(`/${href}`, '_self');
    }
    setLoading(true);

    try {
      const { type } = await callService('SearchesService', 'searchesUrl', {
        s: formatArrayToString(href.split('?')[0]),
      });

      if (type) setTabType(type);
    } catch (error) {
      setLoading(false);
    }
  };

  const getSelectedContent = (
    s: number,
    onHrefUpdate: (href: string) => void
  ) => {
    switch (s) {
      case BOOKER_TABS.HOTEL:
        return (
          <HotelTabContent
            onHrefUpdate={onHrefUpdate}
            href={href}
            onSearchCalled={handleSearchButtonClick}
            type={tabType}
            error={hotelError}
            initialError={initialHotelError}
            setError={setHotelError}
            setInitialError={setInitialHotelError}
          />
        );
      case BOOKER_TABS.TOUR:
        return (
          <BookerWheretoGo
            onHrefUpdate={onHrefUpdate}
            subtitle='Yurt İçi Kültür Turları'
            href={href}
            onSearchCalled={handleSearchButtonClick}
            onInputLocationChange={addTourSearchBarItemClickGTM}
            type={tabType}
            error={tourError}
            initialError={initialTourError}
            setError={setTourError}
            setInitialError={setInitialTourError}
          />
        );
      case BOOKER_TABS.FLIGHT:
        return (
          <FlightTabContent
            onHrefUpdate={onHrefUpdate}
            error={flightError}
            initialError={initialFlightError}
            href={href}
            setError={setFlightError}
            setInitialError={setInitialFlightError}
          />
        );
      case BOOKER_TABS.CRUISE:
        return (
          <CruiseTabContent
            onHrefUpdate={onHrefUpdate}
            subtitle='Ülke, şehir, bölge, liman veya tur adı '
            href={href}
            onSearchCalled={handleSearchButtonClick}
            type={tabType}
          />
        );
    }
    return null;
  };
  const bookerRef = useRef<HTMLDivElement | null>(null);
  useOutsideClick(bookerRef, () => {
    setHotelError({});
    setFlightError({});
  });

  const bottomMarginBooker = selected === 1 || selected === 3 ? 100 : 0;
  return (
    <HomePageBookerContainer bottom={bottomMarginBooker}>
      <BookerContainer hasError={hasError} ref={bookerRef}>
        <Tab selected={selected} onTabSelection={onTabSelect}>
          {BookerTabItems.filter((item) => item.disabled !== true).map(
            (item, index) => (
              <TabItem key={index} text={item.title} icon={item.icon} />
            )
          )}
        </Tab>
        {getSelectedContent(selected, onHrefUpdate)}
        <BookerSearchButton onClick={handleSearchButtonClick} />
      </BookerContainer>
      {loading && <Loader />}
    </HomePageBookerContainer>
  );
};

export default Booker;
